/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { faRefresh } from '@fortawesome/free-solid-svg-icons/faRefresh'
import { faPencilRuler } from '@fortawesome/free-solid-svg-icons/faPencilRuler'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import ListCard from '../../../../client/js/components/ListCard'
import DeleteSelectionModal from '../../../../client/js/components/modals/DeleteSelectionModal'
import PageTitle from '../../../../client/js/components/PageTitle'
import SelectionAlert from '../../../../client/js/components/selection/SelectionAlert'
import useSelection from '../../../../client/js/components/selection/useSelection'
import useQueryParams from '../../../../client/js/hooks/useQueryParams'
import RequirePerm from '../../../auth/components/RequirePerm'
import {
  deleteManagementReport,
  fetchManagementReports,
  fetchManagementReportYears
} from '../../lib/rest/managementReportAPIClient'
import CustomerInput from '../inputs/CustomerInput'
import DownloadReportsButton from './DownloadReportsButton'
import GenerateReportsButton from './GenerateReportsButton'
import ManagementReportList from './ManagementReportList'
import usePaginationQuery from '../../../../client/js/components/pagination/usePaginationQuery'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import useParsedQueryParam from '../../../../client/js/hooks/useParsedQueryParam'
import { parseNumber } from '@jalik/form-parser'
import { getTemplatesSettingsLink } from '../../lib/links/settingsLinks'
import Filters from '../../../../client/js/components/Filters'
import Filter from '../Filter'
import { Button, Container, Select } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import Toolbar from '../../../../client/js/components/Toolbar'
import Page from '../../../../client/js/components/Page'

const initialQuery = { limit: 10 }

function ManagementReportListPage () {
  const intl = useIntl()

  const [searchParams, setSearchParams] = useQueryParams()
  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const customerId = useParsedQueryParam('customerId', parseNumber)
  const estateId = useParsedQueryParam('estateId', parseNumber)
  const year = useParsedQueryParam('year', parseNumber)

  const reportsQuery = useQuery({
    enabled: true,
    queryKey: ['managementReports', page, limit, searchParams],
    queryFn: () => fetchManagementReports({
      ...initialQuery,
      ...searchParams,
      page,
      limit
    }).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })
  const items = useMemo(() => (
    (reportsQuery.data != null ? getHalEmbedded(reportsQuery.data)?.managementReports : null) || []
  ), [reportsQuery.data])

  const yearsQuery = useQuery({
    enabled: true,
    queryKey: ['managementReportsYears'],
    queryFn: async () => fetchManagementReportYears()
  })
  const years = useMemo(() => (
    (yearsQuery.data?.data != null ? yearsQuery.data.data : null) || [] as number[]
  ), [yearsQuery.data])

  // Items selection.
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const selection = useSelection<number>({
    multiple: true,
    items: selectedItems,
    onChange: setSelectedItems,
    source: items.map((el) => el.id)
  })

  // Items deletion.
  const handleDelete = useCallback((id: number) => deleteManagementReport(id), [])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((s) => !s)
  }, [])

  // Filters
  const customerFilter = useMemo(() => ({
    isLessor: true
  }), [])

  const downloadFilter = useMemo(() => ({
    ids: selection.items.length > 0 ? selection.items : undefined,
    customerId,
    estateId,
    year
  }), [customerId, estateId, selection.items, year])

  const setFilter = useCallback((name: string, value: string) => {
    setSearchParams((s) => ({ ...s, page: '1', [name]: value }))
  }, [setSearchParams])

  const handleValueChange = useCallback((name: string) => (value: string) => {
    setFilter(name, value)
  }, [setFilter])

  const title = intl.formatMessage({
    id: 'management_reports',
    defaultMessage: 'Rapports de gérance'
  })

  return (
    <Page>
      <Container size="xl">
        <PageTitle
          hidden
          title={title}
        />

        <ListCard
          filters={<Filters in>
            <Filter>
              <CustomerInput
                queryFilter={customerFilter}
                label={intl.formatMessage({
                  id: 'owner',
                  defaultMessage: 'Propriétaire'
                })}
                name="customerId"
                onChange={handleValueChange('customerId')}
                value={customerId != null ? String(customerId) : null}
              />
            </Filter>
            <Filter>
              <Select
                label={intl.formatMessage({
                  id: 'year',
                  defaultMessage: 'Année'
                })}
                name="year"
                onChange={handleValueChange('year')}
                required
                value={year != null ? String(year) : ''}
                data={[
                  // {
                  //   value: '',
                  //   label: intl.formatMessage({
                  //     id: 'select_year',
                  //     defaultMessage: 'Sélectionner une année'
                  //   })
                  // },
                  ...years.map((value) => ({
                    value: String(value),
                    label: String(value)
                  }))
                ]}
              />
            </Filter>
          </Filters>}
          pagination={pagination}
          selection={selection}
          selectionActions={<SelectionAlert>
            <RequirePerm
              permissions={['write:management-reports']}
              render={() => (
                <Button onClick={toggleDeleteModal}>
                  <FontAwesomeIcon
                    fixedWidth
                    icon={faTrash}
                  />
                </Button>
              )}
            />
          </SelectionAlert>}
          toolbar={<Toolbar>
            <Button
              component={AnchorLink}
              variant="filled"
              to="view"
            >
              {intl.formatMessage({
                id: 'consult_a_report',
                defaultMessage: 'Consulter un rapport'
              })}
            </Button>
            <GenerateReportsButton showLabel />
            <Button
              disabled={reportsQuery.isFetching}
              loading={reportsQuery.isFetching}
              onClick={() => {
                reportsQuery.refetch()
              }}
              title={intl.formatMessage({
                id: 'refresh',
                defaultMessage: 'Rafraîchir'
              })}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faRefresh}
              />
            </Button>
            <DownloadReportsButton
              disabled={reportsQuery.isFetching || items.length === 0}
              queryFilter={downloadFilter}
            />
            <Button
              component={AnchorLink}
              title={intl.formatMessage({
                id: 'report_templates',
                defaultMessage: 'Modèles de rapport'
              })}
              to={`${getTemplatesSettingsLink()}#management-report`}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faPencilRuler}
              />
            </Button>
          </Toolbar>}
        >
          <LoadingHelper
            query={reportsQuery}
            render={() => (items
              ? <ManagementReportList items={items} selectable />
              : null)}
          />
        </ListCard>
      </Container>

      {isDeleteModalOpen
        ? <DeleteSelectionModal
          opened={isDeleteModalOpen}
          items={selection.items}
          onConfirm={handleDelete}
          onSuccess={() => {
            selection.clear()
            reportsQuery.refetch()
          }}
          onClose={toggleDeleteModal}
        />
        : null}
    </Page>
  )
}

export default ManagementReportListPage
